// src/App.jsx
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { useDarkMode } from "./hooks/useDarkMode";
import { useScrollLock } from "./hooks/useScrollLock";
import { useAuth } from "./hooks/useAuth";
import { usePosts } from "./hooks/usePosts";
import { useTasks } from "./hooks/useTasks";
import { useToast } from "./hooks/useToast";
import { useOrgSettings } from "./hooks/useOrgSettings";
import { useTutorial } from "./hooks/useTutorial";
import { useVendors } from "./hooks/useVendors";
import { usePushNotifications } from "./hooks/usePushNotifications";
import { useBranding } from "./hooks/useBranding";
import { useAssignmentRules } from "./hooks/useAssignmentRules";
import { useLocations } from "./hooks/useLocations";
import { useNotifications } from "./hooks/useNotifications";
import NotificationPanel from "./components/NotificationPanel";
import { suggestTaskFromPost } from "./lib/claudeApi";
import AuthPage from "./components/AuthPage";
import LandingPage from "./components/LandingPage";
import PendingApproval from "./components/PendingApproval";
import PostForm from "./components/PostForm";
import Board from "./components/Board";
import Dashboard from "./components/Dashboard";
import AdminPanel from "./components/AdminPanel";
import MyTasks from "./components/MyTasks";
import OrgOnboarding from "./components/OrgOnboarding";
import DisabledAccount from "./components/DisabledAccount";
import CookieBanner from "./components/CookieBanner";
import AppFooter from "./components/AppFooter";
import AppHeader from "./components/AppHeader";
import AppNav from "./components/AppNav";
import PinchIcon from "./components/PinchIcon";
import MobileModal from "./components/MobileModal";
import Toast from "./components/Toast";
import Tutorial from "./components/Tutorial";
import "./styles/index.css";
import "./styles/tokens.css";
import "./styles/components.css";


export default function App({ inviteToken = "" }) {
  const [dark, toggleDark] = useDarkMode();
  const { user, profile, isAdmin, isSuperUser, authLoading, profileLoading, login, register, logout } = useAuth();
  usePushNotifications(user?.uid);
  const [pendingOnboarding, setPendingOnboarding] = useState(false);
  const [pendingOrgName, setPendingOrgName] = useState("");
  const { posts, resolvedPosts, status, addPost, deletePost, upvotePost, dismissFlag, reportPost, updatePostStatus } = usePosts(profile?.orgId);
  const { tasks, createTask, assignTask, assignSecondaryTask, acceptTask, acceptSecondaryTask, denyTask, denySecondaryTask, completeTask, deleteTask, createDraftTasks, approveDraftTask } = useTasks(profile?.orgId);
  const { vendors } = useVendors(profile?.orgId);
  const { orgName, guidelines, guidelineItems, categories, anonSubmitEnabled, updateGuidelines, updateCategories, updateAnonSubmitEnabled } = useOrgSettings(profile?.orgId);
  const { branding, updateBranding } = useBranding(profile?.orgId);
  const { rules: assignmentRules } = useAssignmentRules(profile?.orgId);
  const { locations } = useLocations(profile?.orgId);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications(user?.uid);
  const [notifOpen, setNotifOpen] = useState(false);
  const allPosts = [...posts, ...resolvedPosts];
  const { toast, showToast } = useToast();
  const { tutorialEnabled, setTutorialEnabled } = useTutorial(user?.uid);

  const navigate = useNavigate();
  const location = useLocation();
  const PATH_VIEW = { "/logs": "today", "/dashboard": "dashboard", "/tasks": "mytasks", "/admin": "admin" };
  const VIEW_PATH = { today: "/logs", dashboard: "/dashboard", mytasks: "/tasks", admin: "/admin" };
  const view = PATH_VIEW[location.pathname] || "today";
  const setView = (v) => navigate(VIEW_PATH[v] || "/logs");

  const [modalOpen, setModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState(inviteToken ? "register" : null); // null | "login" | "register"

  const handleRegister = async (email, password, displayName, orgMode, orgNameVal, orgId) => {
    await register(email, password, displayName, orgMode, orgNameVal, orgId);
    if (orgMode === "create") {
      setPendingOrgName(orgNameVal);
      setPendingOnboarding(true);
    }
  };

  if (authLoading || profileLoading) {
    return (
      <div className="auth-splash">
        <div className="auth-splash-inner">
          <div className="live-dot" style={{ width: 12, height: 12 }} />
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  if (!user || !profile) {
    if (!authMode) return <LandingPage onSignIn={() => setAuthMode("login")} onRegister={() => setAuthMode("register")} />;
    return <AuthPage initialMode={authMode} inviteToken={inviteToken} onLogin={login} onRegister={handleRegister} onBack={() => setAuthMode(null)} />;
  }

  if (profile.disabled) {
    return (
      <DisabledAccount
        displayName={profile.displayName || user.email}
        onLogout={logout}
      />
    );
  }

  if (profile.orgStatus === "pending") {
    return (
      <PendingApproval
        displayName={profile.displayName || user.email}
        orgName={profile.orgName}
        onLogout={logout}
      />
    );
  }

  if (pendingOnboarding && isSuperUser) {
    return (
      <OrgOnboarding
        orgId={profile.orgId}
        orgName={pendingOrgName || orgName}
        onComplete={() => setPendingOnboarding(false)}
      />
    );
  }

  const handleUpvote = async (id, uid, hasVoted) => {
    try { await upvotePost(id, uid, hasVoted); }
    catch { showToast("Upvote failed.", true); }
  };

  const handleDelete = async (id) => {
    try { await deletePost(id); showToast("Post deleted."); }
    catch { showToast("Could not delete.", true); }
  };

  const handleDismissFlag = async (id) => {
    try { await dismissFlag(id); showToast("Flag dismissed."); }
    catch { showToast("Could not dismiss flag.", true); }
  };

  const handleReport = async (postId, reason) => {
    try {
      await reportPost(postId, user.uid, displayName, reason);
      showToast("Report submitted.");
    } catch {
      showToast("Could not submit report.", true);
    }
  };

  const handleUpdatePostStatus = async (postId, newStatus, opts = {}) => {
    try {
      await updatePostStatus(postId, newStatus, { ...opts, actorUid: user.uid, actorName: displayName });
      const msgs = { open: "Post reopened.", investigating: "Post marked investigating.", resolved: "Post resolved.", dismissed: "Post dismissed." };
      showToast(msgs[newStatus] || "Status updated.");
    } catch {
      showToast("Could not update status.", true);
    }
  };

  const handleSmartTaskFromPost = async (post) => {
    showToast("Generating smart task…");
    try {
      const suggestion = await suggestTaskFromPost(post, vendors);
      await createTask({
        title: suggestion.title,
        description: suggestion.description,
        assignedTo: null,
        assignedToName: null,
        postIds: [post.id],
        category: post.category,
        severity: post.severity,
        rules: assignmentRules,
      });
      showToast("Smart task created!");
    } catch {
      showToast("Could not create smart task.", true);
    }
  };

  const handleCompleteTask = async (taskId, postIds) => {
    try { await completeTask(taskId, postIds); showToast("Task completed — posts resolved."); }
    catch { showToast("Could not complete task.", true); }
  };

  const handleModalOpen = () => { setModalOpen(true); document.body.style.overflow = "hidden"; };
  const handleModalClose = () => { setModalOpen(false); document.body.style.overflow = ""; };

  const loading = status === "connecting";
  const displayName = profile?.displayName || user?.displayName || user?.email;
  const myTaskCount = tasks.filter((t) => t.status === "open" && (t.assignedTo === user.uid || t.assignedTo2 === user.uid)).length;

  // Client-side location filtering — null means show all
  const locationFilteredPosts = selectedLocationId
    ? posts.filter((p) => p.locationId === selectedLocationId)
    : posts;
  const locationFilteredResolved = selectedLocationId
    ? resolvedPosts.filter((p) => p.locationId === selectedLocationId)
    : resolvedPosts;

  const NAV_TABS = [
    { id: "today",     label: "Live Logs" },
    { id: "dashboard", label: "Dashboard" },
    { id: "mytasks",   label: "My Tasks", badge: myTaskCount || null },
    ...(isAdmin ? [{ id: "admin", label: "⚙ Admin" }] : []),
  ];

  return (
    <>
      <AppHeader
        productName={branding?.productName}
        logoUrl={branding?.logoUrl}
        orgName={orgName}
        locations={locations}
        selectedLocationId={selectedLocationId}
        onSelectLocation={setSelectedLocationId}
        unreadCount={unreadCount}
        onOpenNotifications={() => setNotifOpen((o) => !o)}
        userName={displayName}
        isAdmin={isAdmin}
        isSuperUser={isSuperUser}
        dark={dark}
        onToggleDark={toggleDark}
        onLogout={logout}
      />
      {notifOpen && (
        <NotificationPanel
          uid={user?.uid}
          notifications={notifications}
          unreadCount={unreadCount}
          markRead={markRead}
          markAllRead={markAllRead}
          onClose={() => setNotifOpen(false)}
        />
      )}

      <AppNav
        tabs={NAV_TABS}
        activeId={view}
        onChange={setView}
      />

      {view === "today" && (
        <div className="page-layout">
          <aside className="sidebar">
            <div data-tut="post-form-box">
              <PostForm user={user} profile={profile} guidelines={guidelines} categories={categories} locations={locations} defaultLocationId={selectedLocationId} onSubmit={addPost} onAfterSubmit={(msg, isErr) => showToast(msg, isErr)} />
            </div>
          </aside>
          <div className="board-area">
            <div className="board-header" data-tut="board-header">
              <div className="board-header-title">Live Logs</div>
              <div className="board-header-sub">All unresolved reports</div>
            </div>
            <Board posts={locationFilteredPosts} currentUid={user.uid} currentName={displayName} isAdmin={isAdmin} onUpvote={handleUpvote} onDelete={handleDelete} onReport={handleReport} onSmartTask={isAdmin ? handleSmartTaskFromPost : undefined} onUpdateStatus={isAdmin ? handleUpdatePostStatus : undefined} loading={loading} orgId={profile?.orgId} categories={categories} />
          </div>
        </div>
      )}

      {view === "dashboard" && (
        <div className="page-layout">
          <aside className="sidebar">
            <PostForm user={user} profile={profile} guidelines={guidelines} categories={categories} locations={locations} defaultLocationId={selectedLocationId} onSubmit={addPost} onAfterSubmit={(msg, isErr) => showToast(msg, isErr)} />
          </aside>
          <div className="board-area">
            <div className="board-header">
              <div className="board-header-title">Dashboard</div>
              <div className="board-header-sub">Filter by category, date range, and severity</div>
            </div>
            <Dashboard posts={locationFilteredPosts} currentUid={user.uid} currentName={displayName} isAdmin={isAdmin} orgId={profile?.orgId} onUpvote={handleUpvote} onDelete={handleDelete} onReport={handleReport} onSmartTask={isAdmin ? handleSmartTaskFromPost : undefined} onUpdateStatus={isAdmin ? handleUpdatePostStatus : undefined} loading={loading} categories={categories} tasks={tasks} resolvedPosts={locationFilteredResolved} />
          </div>
        </div>
      )}

      {view === "mytasks" && (
        <div className="page-layout-full">
          <MyTasks
            tasks={tasks}
            currentUid={user.uid}
            completeTask={handleCompleteTask}
            allPosts={allPosts}
            acceptTask={acceptTask}
            denyTask={denyTask}
            acceptSecondaryTask={acceptSecondaryTask}
            denySecondaryTask={denySecondaryTask}
            showToast={showToast}
          />
        </div>
      )}

      {view === "admin" && isAdmin && (
        <div className="page-layout-full">
          <AdminPanel
            currentUid={user.uid}
            orgId={profile.orgId}
            orgName={orgName}
            isSuperUser={isSuperUser}
            allPosts={allPosts}
            resolvedPosts={resolvedPosts}
            tasks={tasks}
            createTask={createTask}
            assignTask={assignTask}
            assignSecondaryTask={assignSecondaryTask}
            deleteTask={deleteTask}
            createDraftTasks={createDraftTasks}
            approveDraftTask={approveDraftTask}
            onUpvote={handleUpvote}
            onDelete={handleDelete}
            onDismissFlag={handleDismissFlag}
            onReport={handleReport}
            guidelines={guidelines}
            guidelineItems={guidelineItems}
            categories={categories}
            anonSubmitEnabled={anonSubmitEnabled}
            updateGuidelines={updateGuidelines}
            updateCategories={updateCategories}
            updateAnonSubmitEnabled={updateAnonSubmitEnabled}
            branding={branding}
            updateBranding={updateBranding}
          />
        </div>
      )}

      <AppFooter branding={branding} />
      <MobileModal
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={addPost}
        onAfterSubmit={(msg, isErr) => { showToast(msg, isErr); }}
        user={user}
        profile={profile}
        guidelines={guidelines}
        categories={categories}
        locations={locations}
        defaultLocationId={selectedLocationId}
      />

      <button className="fab" data-tut="fab" onClick={handleModalOpen}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        Add Log
      </button>

      <Toast msg={toast.msg} isErr={toast.isErr} visible={toast.visible} />
      <CookieBanner />

      {tutorialEnabled && (
        <Tutorial
          isAdmin={isAdmin}
          onDismiss={() => setTutorialEnabled(false)}
          onSetView={setView}
          currentView={view}
        />
      )}
    </>
  );
}

function PersonalInfoModal({ displayName, email, isAdmin, isSuperUser, profile, orgName, onClose }) {
  useScrollLock();
  return createPortal(
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-modal-header">
          <div className="profile-modal-title">Personal Information</div>
          <button className="profile-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="profile-modal-body">
          <div className="profile-field">
            <div className="profile-field-label">Full Name</div>
            <div className="profile-field-value">{displayName || "—"}</div>
          </div>
          <div className="profile-field">
            <div className="profile-field-label">Email</div>
            <div className="profile-field-value">{email || "—"}</div>
          </div>
          <div className="profile-field">
            <div className="profile-field-label">Organization</div>
            <div className="profile-field-value">{orgName || "—"}</div>
          </div>
          <div className="profile-field">
            <div className="profile-field-label">Role</div>
            <div className="profile-field-value">
              <span className={`role-badge role-${isSuperUser ? "superuser" : isAdmin ? "admin" : "user"}`}>
                {isSuperUser ? "Super User" : isAdmin ? "Admin" : "User"}
              </span>
            </div>
          </div>
          {profile?.createdAt?.toDate && (
            <div className="profile-field">
              <div className="profile-field-label">Member Since</div>
              <div className="profile-field-value">
                {profile.createdAt.toDate().toLocaleDateString("en-US", {
                  month: "long", day: "numeric", year: "numeric",
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
